import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Fingerprint, Loader2, ShieldCheck, Undo2 } from 'lucide-react';
import { agentApi } from '../agentApi';
import { toolLabel } from '../agentContextMap';
import { formatSeconds, formatTime, shortHash, timeAgo } from '../format';
import { ACTION_STATUS_LABEL, StatusIcon } from './ApprovalCard';
import FileCard from './FileCard';

function FilesTab({ agentSession }) {
  const sessionId = agentSession.session?.id;
  const exports = agentSession.events.filter((event) => event.type === 'TOOL_RESULT' && event.payload?.name === 'export_data_file').length;
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    setError(null);
    agentApi
      .listFiles(sessionId)
      .then((data) => {
        if (!cancelled) setFiles(data.files || []);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, exports]);

  if (error) return <p className="dagent-muted dagent-pad">{error}</p>;
  if (!files) return <p className="dagent-muted dagent-pad">Loading files…</p>;
  if (files.length === 0) {
    return <p className="dagent-muted dagent-pad">No files yet. Ask the agent to export a list or report as CSV or PDF.</p>;
  }
  return (
    <div className="dagent-files-list dagent-pad">
      {files.map((file) => (
        <FileCard key={file.fileId} file={file} />
      ))}
    </div>
  );
}

function ActionsTab({ agentSession, onUndoRun }) {
  const { actions, runs, readOnly, undoAction } = agentSession;
  const groups = useMemo(() => {
    const byRun = new Map();
    for (const action of [...actions].reverse()) byRun.set(action.runId, [...(byRun.get(action.runId) || []), action]);
    return [...byRun.entries()];
  }, [actions]);

  const undo = async (id, force) => {
    const response = await undoAction(id, force).catch((error) => {
      toast.error(error.message);
      return null;
    });
    if (response?.outcome && !response.outcome.ok) toast.warn(response.outcome.error);
    else if (response) toast.success('Undone');
  };

  if (groups.length === 0) return <p className="dagent-muted dagent-pad">No actions proposed in this conversation yet.</p>;

  return (
    <div className="dagent-inspector__list">
      {groups.map(([runId, runActions]) => {
        const run = runs.find((candidate) => candidate.id === runId);
        const undoable = runActions.filter((action) => action.canUndo).length;
        return (
          <div key={runId} className="dagent-run-group">
            <div className="dagent-run-group__head">
              <span>Run {runId.slice(-6)}</span>
              <span className="dagent-muted">{run ? timeAgo(run.startedAt) : ''}</span>
              {undoable > 1 && !readOnly && (
                <button type="button" className="dagent-link-btn" onClick={() => onUndoRun(runId)}>
                  <Undo2 size={12} aria-hidden="true" /> Undo run
                </button>
              )}
            </div>
            {runActions.map((action) => (
              <div key={action.id} className={`dagent-action-row is-${action.status.toLowerCase()}`}>
                <StatusIcon status={action.status} />
                <div className="dagent-action-row__main">
                  <div className="dagent-action-row__summary">{action.summary}</div>
                  <div className="dagent-muted dagent-small">
                    {toolLabel(action.toolName)} · {ACTION_STATUS_LABEL[action.status] || action.status}
                    {action.executedAt ? ` · ${timeAgo(action.executedAt)}` : ''}
                    {action.risk === 'EXTERNAL' ? ' · external' : ''}
                  </div>
                  {action.error && action.status !== 'REJECTED' && <div className="dagent-approval__error">{action.error}</div>}
                  {action.undoResult?.error && action.status === 'SUCCEEDED' && <div className="dagent-approval__error">Undo refused: {action.undoResult.error}</div>}
                </div>
                {action.canUndo && !readOnly && (
                  <div className="dagent-action-row__buttons">
                    <button type="button" className="dagent-btn dagent-btn--ghost dagent-btn--sm" onClick={() => undo(action.id)}>
                      Undo
                    </button>
                    {action.undoResult?.conflict && (
                      <button type="button" className="dagent-btn dagent-btn--danger dagent-btn--sm" onClick={() => undo(action.id, true)}>
                        Force
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function LedgerTab({ agentSession }) {
  const { events, session } = agentSession;
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [openSeq, setOpenSeq] = useState(null);
  const [fullEvent, setFullEvent] = useState(null);

  const verify = async () => {
    setVerifying(true);
    try {
      setVerification((await agentApi.verify(session.id)).verification);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setVerifying(false);
    }
  };

  const toggle = async (event) => {
    if (openSeq === event.seq) {
      setOpenSeq(null);
      return;
    }
    setOpenSeq(event.seq);
    setFullEvent(null);
    if (event.payload?.resultTooLarge) {
      try {
        setFullEvent((await agentApi.getEvent(session.id, event.seq)).event);
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <div className="dagent-ledger">
      <div className="dagent-ledger__head">
        <button type="button" className="dagent-btn dagent-btn--ghost dagent-btn--sm" onClick={verify} disabled={verifying || !session}>
          {verifying ? <Loader2 size={13} className="dagent-spin" aria-hidden="true" /> : <Fingerprint size={13} aria-hidden="true" />} Verify chain
        </button>
        <span className="dagent-muted dagent-small">{events.length} events, append-only</span>
      </div>
      {verification && (
        <div className={`dagent-banner ${verification.ok ? 'dagent-banner--green' : 'dagent-banner--danger'}`}>
          <ShieldCheck size={14} aria-hidden="true" />
          {verification.ok
            ? `Chain intact: ${verification.checked} events, head ${shortHash(verification.headHash)}`
            : `Chain broken at event ${verification.brokenAtSeq}: ${verification.reason}`}
        </div>
      )}
      <ol className="dagent-ledger__list">
        {[...events].reverse().map((event) => (
          <li key={event.seq} className={openSeq === event.seq ? 'is-open' : ''}>
            <button type="button" onClick={() => toggle(event)}>
              <span className="dagent-ledger__seq">#{event.seq}</span>
              <span className="dagent-ledger__type">{event.type.replace(/_/g, ' ').toLowerCase()}</span>
              <span className="dagent-ledger__actor">{event.actorType.toLowerCase()}</span>
              <code className="dagent-ledger__hash">{shortHash(event.hash)}</code>
            </button>
            {openSeq === event.seq && (
              <div className="dagent-ledger__detail">
                <div className="dagent-small dagent-muted">
                  {formatTime(event.createdAt)} · prev {shortHash(event.prevHash)}
                </div>
                <pre>{JSON.stringify((fullEvent && fullEvent.seq === event.seq ? fullEvent : event).payload, null, 2).slice(0, 30000)}</pre>
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ContextTab({ agentSession, pageContext }) {
  const { runs, session } = agentSession;
  return (
    <div className="dagent-pad">
      <h5 className="dagent-subhead">This screen</h5>
      <pre className="dagent-json">{JSON.stringify(pageContext, null, 2)}</pre>
      {session && (
        <>
          <h5 className="dagent-subhead">Runs</h5>
          <table className="dagent-table dagent-table--compact">
            <thead>
              <tr>
                <th>Started</th>
                <th>Status</th>
                <th>Steps</th>
                <th>Model</th>
                <th>Tokens</th>
              </tr>
            </thead>
            <tbody>
              {[...runs].reverse().map((run) => (
                <tr key={run.id}>
                  <td>{timeAgo(run.startedAt)}</td>
                  <td>{run.status.toLowerCase().replace('_', ' ')}</td>
                  <td>{run.steps}</td>
                  <td>{run.profile?.model || '—'}</td>
                  <td>
                    {run.usage ? `${run.usage.inputTokens || 0} / ${run.usage.outputTokens || 0}` : '—'}
                    {run.usage?.providerMs ? ` · ${formatSeconds((run.usage.providerMs + (run.usage.toolMs || 0)) / 1000)}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function InspectorPanel({ controller, pageContext }) {
  const [tab, setTab] = useState('actions');
  const { agentSession, undoRun } = controller;
  const pending = agentSession.actions.filter((action) => action.status === 'PENDING_APPROVAL').length;

  return (
    <aside className="dagent-inspector">
      <div className="dagent-segmented" role="tablist">
        {[
          ['actions', `Actions${pending ? ` (${pending})` : ''}`],
          ['files', 'Files'],
          ['ledger', 'Ledger'],
          ['context', 'Context'],
        ].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>
      <div className="dagent-inspector__body">
        {!agentSession.session && <p className="dagent-muted dagent-pad">Start or pick a conversation.</p>}
        {agentSession.session && tab === 'actions' && <ActionsTab agentSession={agentSession} onUndoRun={undoRun} />}
        {agentSession.session && tab === 'files' && <FilesTab agentSession={agentSession} />}
        {agentSession.session && tab === 'ledger' && <LedgerTab agentSession={agentSession} />}
        {tab === 'context' && <ContextTab agentSession={agentSession} pageContext={pageContext} />}
      </div>
    </aside>
  );
}

export default InspectorPanel;
